(ns ncr.reports.grid-cell
  (:require
   [ncr.widgets.grid :as grid]
   [ncr.fragment.props.money :as money]
   [ncr.widgets.util :as wu]
   [ncr.widgets.core :as w]
   [ncr.reports.common :as rc]))


(defn copy-to-value [h & {:keys [from]}]
  (fn [ctx]
    (h (assoc ctx :value (get ctx from)))))

(defn cell-extended-background [next & {:keys [extended?] :or {extended? some?}}]
  (fn [{:keys [cell-context] :as ctx}]
    (let [ext-data ((-> cell-context grid/table-meta :getExtendedData) cell-context)]
      (if (extended? ext-data)
        [{:class :ncr-m-extended} (next ctx)]
        (next ctx)))))

(defn cell-extend-on-click [next & {:keys [guard data extended?] :or {guard (constantly true) extended? some?}}]
  (fn [{:keys [cell-context] :as ctx}]
    (let
      [toggle-row-extended (-> cell-context grid/table-meta :toggleRowExtended (partial cell-context))
       ext-data ((-> cell-context grid/table-meta :getExtendedData) cell-context)]
      [{:class (when (guard ctx) :ncr-extendable)
        :on-click
        (fn [_]
          (if-not (extended? ext-data)
            (when
              (and (-> cell-context grid/value :extension) (guard ctx))
              (toggle-row-extended data))
            (toggle-row-extended)))} (next ctx)])))

(defn colorize [h & {:keys [indicator guard expense-class income-class]
                     :or {guard (constantly true) indicator :value
                          expense-class :m-expense-color income-class :m-income-color}}]
  (fn [ctx]
    (if (guard ctx)
      [{:class
        (when-let [value (get ctx indicator)]
          (if (neg? value) expense-class income-class))}
       (h ctx)]
      (h ctx))))

(defn colorize-border [h & {:keys [guard indicator] :or {guard (constantly true) indicator :value}}]
  (fn [ctx]
    (if (guard ctx)
      [{:class
        (when-let [value (get ctx indicator)]
          (if (neg? value) :m-expense-border :m-income-border))}
       (h ctx)]
      (h ctx))))

(defn expandable-heading [h]
  (fn [cell-context]
    (let
      [^js row (grid/row cell-context)
       depth (.-depth row)
       has-children? (seq (.-subRows row))
       collapsed? (not (.getIsExpanded row))
       can-expand (and has-children? (.getCanExpand row))]
      [{:on-click
        (fn [_]
          (when can-expand
            (.toggleExpanded row collapsed?)))}
       [:div.ncr-grid-heading
        {:class (when can-expand :ncr-m-cursor-pointer)
         :style {:padding-left (str (* depth 20) "px") :white-space :no-wrap}}
        (when can-expand
          [w/c-mdi-icon
           {:class :ncr-grid-row-expander
            :icon (if collapsed? :chevron-right :chevron-down)
            :style {:margin-left -4}}])
        h]])))

(defn- value-or-zero [h & {:keys [guard] :or {guard (constantly true)}}]
  (fn [ctx]
    (h
      (if (guard ctx)
        (update ctx :value #(or % money/zero))
        ctx))))

(defn- stringify-value [h & {:keys [default]}]
  (fn [ctx]
    (h (update ctx :value #(money/to-string % default)))))

(defn- extended? [kind]
  #(= kind (get % :kind ::not-found)))

(defn- money-cell [path {:keys [colorize? kind column] :as ctx}]
  ((-> :value
     (stringify-value :default "\u2014")
     (colorize :guard (constantly colorize?)
       :income-class :ncr-u-money-income
       :expense-class :ncr-u-money-expense)
     (cell-extended-background :extended? (extended? kind))
     (cell-extend-on-click
       :extended? (extended? kind)
       :data
       {:path path
        :kind kind
        :column column})
     (value-or-zero :guard (fn [{:keys [kind]}] (not= :estimated kind))))
   ctx))

(defn- relative-estimate-cell [path {:keys [kind column] :as ctx}]
  (let
    [compute
     (fn [h]
       (fn [{:keys [values] :as ctx}]
         (let
           [^js actual-value (:actual values)
            ^js estimated-value (:estimated values)]
           (h
             (assoc ctx :value
               (when (and (some? estimated-value) (not (.isZero estimated-value)))
                 (-> (or actual-value money/zero)
                   (.div estimated-value)
                   (.times 100))))))))
     render
     (fn [{:keys [value]}]
       [{:class (when-not (some? value) :ncr-report-estimate__no-estimate)}
        [:span
         (if (some? value)
           (str (money/to-string value) "%")
           "-")]])
     extended? (extended? kind)]

    ((-> render
       (cell-extended-background :extended? extended?)
       (cell-extend-on-click
         :extended? extended?
         :guard
         (fn [{:keys [value]}]
           (some? value))
         :data
         {:path path
          :kind kind
          :column column})
       (compute))
     ctx)))

(defn- estimated [{:keys [paths values estimates-mode] :as ctx}]
  (let
    [estimated-paths (:estimated paths)
     estimated-value (:estimated values)
     props {:class :ncr-report-estimate}]
    [:tr
     (into [:td]
       (wu/collect-props props
         (case estimates-mode
           :numbers
           (money-cell estimated-paths
             (assoc ctx :value estimated-value))

           :percentages
           (relative-estimate-cell estimated-paths ctx))))]))

(defn actual-and-estimated [{:keys [paths values cell-context] :as ctx}]
  (let
    [actual-paths (:actual paths)
     actual-value (:actual values)]
    [:table.ncr-report-a-and-e
     [:tbody
      [:tr
       (into [:td]
         (wu/collect-props {}
           (money-cell actual-paths
             (assoc ctx :value actual-value :kind :actual))))]

      (when-let [estimates-mode (-> cell-context (grid/table-meta) :preferences :estimates-mode)]
        (estimated (assoc ctx :estimates-mode estimates-mode :kind :estimated)))]]))

(defn data-row [report path {:keys [heading] :as ctx} overrides]
  (let
    [months-paths-fn
     (fn [m]
       (let [base (assoc path :month m)]
         {:actual
          (assoc base :kind :actual)
          :estimated
          (assoc base :kind :estimated)}))

     months-values-fn
     (fn [m]
       (let [paths (months-paths-fn m)]
         {:actual
          (-> report (rc/from-path (:actual paths)) :value)
          :estimated
          (-> report (rc/from-path (:estimated paths)) :value)}))

     cell-fn
     (fn [m]
       (fn [cell-context]
         (let
           [ctx
            (merge ctx
              {:paths (months-paths-fn m)
               :values (months-values-fn m)
               :cell-context cell-context
               :column (case m :total :total [:month m])})]
           (actual-and-estimated ctx))))]
    (merge
      {:id path
       :type :data
       :heading (expandable-heading heading)
       :total (cell-fn :total)
       :months cell-fn

       :total-values (months-values-fn :total)
       :total-paths (months-paths-fn :total)
       :months-paths-fn months-paths-fn
       :months-values-fn months-values-fn}
      overrides)))



